import { IPages, ISites } from '@llws/lift-entity-interfaces';
import { OrderCommand } from '@llws/dr-objectified-query';
import { Equal, IQueryCommandOptions, convertQueryCommandToQueryString } from '@llws/dynamic-router-utils';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { IApiResponse, IMetaResponse, subsetObject } from '@/shared';
import app from '@/main';

/**
 * Fetches Website by ID <br>
 * API Endpoint - `/sites/${id}` <br> via {@link adminRequest.get}
 * @param {number} id - id of the website
 */
export const getById = (id: number) => adminRequest.get<ISites, null>(`/website-api/sites/${id}`, `?`);

/**
 * Fetches Websites, may fetch by  filters <br>
 * API Endpoint - `/website-api/sites?${qs}${(qs && modulesView ? '&' : '')}${modulesView}` <br> via {@link adminRequest.get}
 * @param {Record<string, (string | number)>} [filters] - optional filter query
 */
export const get = (filters?: Record<string, string | number | Array<OrderCommand>>) => {
  let qs: string = ``;

  if (filters) {
    if (filters.limit) {
      qs += `limit=${filters.limit}`;
    }
    if (filters.page) {
      qs += `&page=${filters.page}`;
    }
    if (filters.name) {
      qs += `&where=name~like:${filters.name}`;
    }
    if (filters.statusFilterValue) {
      qs += `&where=status:${filters.statusFilterValue}`;
    }
    if (filters.orderBy) {
      (filters.orderBy as Array<OrderCommand>).forEach((order) => {
        qs += `&orderBy=${order.field}~${order.direction ? order.direction : ''}`;
      });
    }
    // Default filter to exlcudes deleted websites
    qs += `&where=status~not:deleted`;
  }
  const modulesView = !app()?.$rsGlobals.$currentUser.isAdmin || !app()?.$rsGlobals.$currentUser.isSuperUser ? 'view=modules' : '';
  return adminRequest.get<ISites[], IMetaResponse>(`/website-api/sites?${qs}${(qs && modulesView ? '&' : '')}${modulesView}`);
};

/**
 * Fetches website for subheaders <br>
 * API Endpoint - `/website-api/sites/${websiteId}${convertCommandToQueryString(query)}` <br> via {@link adminRequest.get}
 * @param {(string | number)} websiteId - id of the website
 */
export const getForSubheader = async (websiteId: string | number) => {
  const query: IQueryCommandOptions = {
    select: ['id', 'siteUrl', 'name', 'siteKey', 'sourceClientId', 'source.clientKey'],
    relations: [
      { path: 'sourceClient', alias: 'source' },
      {
        path: 'source.liftSettings',
        alias: 'sourceLiftSettings',
        whereCommands: [Equal('sourceLiftSettings.key', 'site_name')],
      },
      { path: 'source.languages', alias: 'sourceClientLanguages' },
      {
        path: 'liftSettings',
        alias: 'siteLiftSettings',
        whereCommands: [Equal('siteLiftSettings.key', 'site_name')],
      },
      { path: 'languages', alias: 'siteLanguages' },
    ],
  };

  const response = await adminRequest.get(`/website-api/sites/${websiteId}${convertQueryCommandToQueryString(query)}`);
  return response;
};

/**
 * Fetches website pages <br>
 * API Endpoint - `/website-api/sites/${websiteId}/pages?${filters.join('&')}` <br> via {@link adminRequest.get}
 * @param {string} clientKey - key of the client
 * @param {number} websiteId - id of the website
 * @param {any} filters - optional filters query
 */
export const getPages = (clientKey: string, websiteId: number, filters: Record<string, string | number | Array<OrderCommand>>) => {
  const qs: string[] = [];

  if (filters) {
    const where: string[] = [];

    where.push('building_id~null'); // do not want building pages to show up

    if (filters.title) {
      where.push(`title~like:${filters.title}`);
    }

    if (filters.status) {
      where.push(`status:${filters.status}`);
    }

    if (filters.showInMenu) {
      where.push(`showInMenu:${filters.showInMenu}`);
    }

    if (where) {
      qs.push(`where=${where.join(',')}`);
    }

    if (filters.limit) {
      qs.push(`limit=${filters.limit}`);
    }

    if (filters.page) {
      qs.push(`page=${filters.page}`);
    }

    if (filters.orderBy) {
      qs.push(`orderBy=${filters.orderBy}`);
    }
  }

  return adminRequest.get(`/website-api/sites/${websiteId}/pages?${qs.join('&')}`);
};

/**
 * Fetches building pages <br>
 * API Endpoint - `/website-api/pages?${qs.join('&')}` <br> via {@link adminRequest.get}
 * @param {string} clientKey - key of the client
 * @param {number} propertyId - id of the property
 * @param {any} filters - optional filters query
 */
export const getBuildingPages = async (clientKey: string, propertyId: number, filters: Record<string, string | number | Array<OrderCommand>>) => {
  const qs: string[] = [];

  if (filters) {
    const where: string[] = [];

    where.push(`building_id:${propertyId}`); // do not want building pages to show up
    where.push(`status~not:deleted`);
    if (filters.title) {
      where.push(`title~like:${filters.title}`);
    }

    if (filters.status) {
      where.push(`status:${filters.status}`);
    }

    if (filters.showInMenu) {
      where.push(`showInMenu:${filters.showInMenu}`);
    }

    if (where) {
      qs.push(`where=${where.join(',')}`);
    }

    if (filters.limit) {
      qs.push(`limit=${filters.limit}`);
    }

    if (filters.page) {
      qs.push(`page=${filters.page}`);
    }
  }

  return adminRequest.get(`/website-api/pages?${qs.join('&')}`);
};

/**
 * Fetches Page by ID <br>
 * API Endpoint - `/website-api/sites/${websiteId}/pages/${pageId}` <br> via {@link adminRequest.get}
 * @param {number} websiteId - id of the website
 * @param {number} pageId - id of the page
 */
export const getPageById = async (websiteId: number, pageId: number) => {
  const result = await adminRequest.get(`/website-api/sites/${websiteId}/pages/${pageId}`);
  return result;
};
/**
 * Fetches page types <br>
 * API Endpoint - '/website-api/page_types' <br> via {@link adminRequest.get}
 */
export const getPageTypes = async (): Promise<{ type: string }[]> => (
  await adminRequest.get('/website-api/page_types')
).data || [];

/**
 * Fetches Page's Select options <br>
 * API Endpoints:
 * <li>`/website-api/sites/${siteId}/pages?select=id,title&where=status~not~deleted&limit=9999` <br> via {@link adminRequest.get}</li>
 * <li>'/website-api/pages?select=id,title&where=status~not~deleted&limit=9999' <br> via {@link adminRequest.get}</li>
 * @param {(string | number)} [siteId] - optional id of the site
 */
export const getPageSelectOptions = async (siteId?: string | number): Promise<IApiResponse<IPages[], any>> => (
  siteId
    ? adminRequest.get(`/website-api/sites/${siteId}/pages?select=id,title&where=status~not~deleted&orderBy=title~asc&limit=9999`)
    : adminRequest.get('/website-api/pages?select=id,title&where=status~not~deleted&orderBy=title~asc&limit=9999')
);

/**
 * Creates | Updates Website Page <br>
 * API Endpoints:
 * <li>`/website-api/sites/${websiteId}/pages` <br> via {@link adminRequest.post}</li>
 * <li>`/website-api/sites/${websiteId}/pages/${data.id}` <br> via {@link adminRequest.put}</li>
 * @param {('post' | 'put')} action - action indicates either to create or update (post/put)
 * @param {number} websiteId - id of the website
 * @param {IPages} data - pages to submit
 */
export const savePage = (action: 'post' | 'put', websiteId: number, data: IPages) => adminRequest[action](
  action === 'post' ? `/website-api/sites/${websiteId}/pages` : `/website-api/sites/${websiteId}/pages/${data.id}`,
  action === 'post' ? subsetObject(data, ['id']) : data,
);
/**
 * Creates | Updates Building Page <br>
 * API Endpoints:
 * <li>`/property-api/properties/${buildingId}/pages` <br> via {@link adminRequest.post}</li>
 * <li> `/property-api/properties/${buildingId}/pages/${data.id}` <br> via {@link adminRequest.put}</li>
 * @param {('post' | 'put')} action - action indicates either to create or update (post/put)
 * @param {number} buildingId - id of the building
 * @param {IPages} data - pages to submit
 */
export const saveBuildingPage = (action: 'post' | 'put', buildingId: number, data: IPages) => adminRequest[action](
  action === 'post' ? `/property-api/properties/${buildingId}/pages` : `/property-api/properties/${buildingId}/pages/${data.id}`,
  action === 'post' ? subsetObject(data, ['id']) : data,
);

/**
 * Deletes Building Page <br>
 * API Endpoint - `/property-api/properties/${buildingId}/pages` <br> via {@link adminRequest.delete}
 * @param {number} buildingId - id of the building
 * @param {number} pageId - id of the page
 */
export const removeBuildingPage = (buildingId: number, pageId: number) => adminRequest.delete(`/property-api/properties/${buildingId}/pages`, [pageId]);
/**
 * Deletes Website Page <br>
 * API Endpoint - `/website-api/sites/${websiteId}/pages` <br> via {@link adminRequest.delete}
 * @param websiteId
 * @param page
 */
export const removePage = (websiteId: number, page: IPages) => adminRequest.put(`/website-api/sites/${websiteId}/pages/${page.id}`, {
  id: page.id,
  siteId: websiteId,
  featuredImage: JSON.stringify(page.featuredImage ?? ''),
  status: 'deleted',
} as IPages);

export default {
  getById,
  saveBuildingPage,
  get,
  getPages,
  removeBuildingPage,
  getBuildingPages,
  getPageById,
  getPageTypes,
  savePage,
  getForSubheader,
  getPageSelectOptions,
  removePage,
};
